import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Badge,
  Grid,
  GridItem,
  Image,
  Divider,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FaQrcode, FaCamera, FaDownload, FaCopy, FaEye, FaTrash, FaLink } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import QRCode from 'qrcode';

export default function QRCodeGenerator({ config }) {
  const [qrData, setQrData] = useState({
    name: '',
    description: '',
    url: '',
    redirect_url: '',
    expires_at: '',
    max_uses: '',
    is_active: true
  });

  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [selectedQR, setSelectedQR] = useState(null);
  const [qrStats, setQrStats] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('qr_codes')
        .select(`
          *,
          qr_visits (
            id,
            visited_at,
            ip_address,
            user_agent,
            referrer
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setQrCodes(data || []);

      // Calculate stats
      const stats = {
        total: data?.length || 0,
        active: data?.filter(qr => qr.is_active).length || 0,
        total_visits: data?.reduce((sum, qr) => sum + (qr.qr_visits?.length || 0), 0) || 0
      };
      setQrStats(stats);

    } catch (error) {
      console.error('Error fetching QR codes:', error);
      toast({
        title: 'Error loading QR codes',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    if (!qrData.name || !qrData.url) {
      toast({
        title: 'Missing required fields',
        description: 'Please enter a name and URL for the QR code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setGenerating(true);

      // Generate unique QR code ID
      const qrId = `qr_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Create the QR code URL that will track visits
      const trackingUrl = `${window.location.origin}/qr/${qrId}`;
      
      // Generate QR code image
      const qrImageDataUrl = await QRCode.toDataURL(trackingUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Save to database
      const qrRecord = {
        id: qrId,
        name: qrData.name,
        description: qrData.description,
        url: qrData.url,
        redirect_url: qrData.redirect_url || qrData.url,
        tracking_url: trackingUrl,
        qr_image_data: qrImageDataUrl,
        expires_at: qrData.expires_at || null,
        max_uses: qrData.max_uses || null,
        is_active: qrData.is_active,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('qr_codes')
        .insert([qrRecord])
        .select()
        .single();

      if (error) throw error;

      setQrImageUrl(qrImageDataUrl);
      setSelectedQR(data);
      onOpen();

      // Reset form
      setQrData({
        name: '',
        description: '',
        url: '',
        redirect_url: '',
        expires_at: '',
        max_uses: '',
        is_active: true
      });

      fetchQRCodes();

      toast({
        title: 'QR Code generated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error generating QR code:', error);
      toast({
        title: 'Error generating QR code',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  const toggleQRStatus = async (qrId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('qr_codes')
        .update({ is_active: !currentStatus })
        .eq('id', qrId);

      if (error) throw error;

      fetchQRCodes();
      toast({
        title: `QR Code ${!currentStatus ? 'activated' : 'deactivated'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating QR code:', error);
      toast({
        title: 'Error updating QR code',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const deleteQRCode = async (qrId) => {
    if (!window.confirm('Are you sure you want to delete this QR code?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', qrId);

      if (error) throw error;

      fetchQRCodes();
      toast({
        title: 'QR Code deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting QR code:', error);
      toast({
        title: 'Error deleting QR code',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const downloadQRCode = (qrData) => {
    const link = document.createElement('a');
    link.download = `qr-code-${qrData.name.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = qrData.qr_image_data;
    link.click();
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading QR codes...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={2}>
          QR Code Generator
        </Text>
        <Text color="gray.600">
          Create QR codes for fax documents with visit tracking
        </Text>
      </Box>

      {/* Stats */}
      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                {qrStats.total}
              </Text>
              <Text fontSize="sm" color="gray.600">Total QR Codes</Text>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold" color="green.500">
                {qrStats.active}
              </Text>
              <Text fontSize="sm" color="gray.600">Active QR Codes</Text>
            </VStack>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                {qrStats.total_visits}
              </Text>
              <Text fontSize="sm" color="gray.600">Total Visits</Text>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      <Grid templateColumns="1fr 1fr" gap={6}>
        {/* Left Panel - Generator */}
        <GridItem>
          <Card>
            <CardHeader>
              <HStack>
                <Icon as={FaQrcode} />
                <Text fontWeight="bold">Generate New QR Code</Text>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Name *</FormLabel>
                  <Input
                    value={qrData.name}
                    onChange={(e) => setQrData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="QR code name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={qrData.description}
                    onChange={(e) => setQrData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Description of what this QR code is for"
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Destination URL *</FormLabel>
                  <Input
                    value={qrData.url}
                    onChange={(e) => setQrData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://example.com"
                    type="url"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Redirect URL (optional)</FormLabel>
                  <Input
                    value={qrData.redirect_url}
                    onChange={(e) => setQrData(prev => ({ ...prev, redirect_url: e.target.value }))}
                    placeholder="Leave empty to use destination URL"
                    type="url"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Expires At (optional)</FormLabel>
                  <Input
                    value={qrData.expires_at}
                    onChange={(e) => setQrData(prev => ({ ...prev, expires_at: e.target.value }))}
                    type="datetime-local"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Max Uses (optional)</FormLabel>
                  <Input
                    value={qrData.max_uses}
                    onChange={(e) => setQrData(prev => ({ ...prev, max_uses: e.target.value }))}
                    placeholder="Leave empty for unlimited"
                    type="number"
                    min="1"
                  />
                </FormControl>

                <Button
                  onClick={generateQRCode}
                  isLoading={generating}
                  loadingText="Generating..."
                  leftIcon={<Icon as={FaQrcode} />}
                  colorScheme="blue"
                  size="lg"
                >
                  Generate QR Code
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </GridItem>

        {/* Right Panel - QR Codes List */}
        <GridItem>
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Text fontWeight="bold">QR Codes</Text>
                <Badge colorScheme="blue">{qrCodes.length} codes</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <TableContainer maxH="500px" overflowY="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Visits</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {qrCodes.map((qr) => (
                      <Tr key={qr.id}>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" fontSize="sm">
                              {qr.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(qr.created_at).toLocaleDateString()}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme="purple">
                            {qr.qr_visits?.length || 0}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={qr.is_active ? 'green' : 'gray'}>
                            {qr.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => {
                                setSelectedQR(qr);
                                setQrImageUrl(qr.qr_image_data);
                                onOpen();
                              }}
                            >
                              <Icon as={FaEye} />
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => downloadQRCode(qr)}
                            >
                              <Icon as={FaDownload} />
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme={qr.is_active ? 'red' : 'green'}
                              onClick={() => toggleQRStatus(qr.id, qr.is_active)}
                            >
                              {qr.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => deleteQRCode(qr.id)}
                            >
                              <Icon as={FaTrash} />
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              {qrCodes.length === 0 && (
                <Text color="gray.500" textAlign="center" py={8}>
                  No QR codes created yet
                </Text>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* QR Code Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={FaQrcode} />
              <Text>QR Code: {selectedQR?.name}</Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              {qrImageUrl && (
                <Box textAlign="center">
                  <Image
                    src={qrImageUrl}
                    alt="Generated QR Code"
                    maxW="300px"
                    mx="auto"
                  />
                </Box>
              )}
              
              {selectedQR && (
                <VStack spacing={3} align="stretch" w="full">
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">Tracking URL:</Text>
                    <HStack>
                      <Input
                        value={selectedQR.tracking_url}
                        readOnly
                        fontSize="sm"
                      />
                      <Button
                        size="sm"
                        leftIcon={<Icon as={FaCopy} />}
                        onClick={() => copyToClipboard(selectedQR.tracking_url)}
                      >
                        Copy
                      </Button>
                    </HStack>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">Destination URL:</Text>
                    <HStack>
                      <Input
                        value={selectedQR.redirect_url}
                        readOnly
                        fontSize="sm"
                      />
                      <Button
                        size="sm"
                        leftIcon={<Icon as={FaCopy} />}
                        onClick={() => copyToClipboard(selectedQR.redirect_url)}
                      >
                        Copy
                      </Button>
                    </HStack>
                  </Box>

                  {selectedQR.description && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Description:</Text>
                      <Text fontSize="sm">{selectedQR.description}</Text>
                    </Box>
                  )}

                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Visits:</Text>
                      <Badge colorScheme="purple" fontSize="lg">
                        {selectedQR.qr_visits?.length || 0}
                      </Badge>
                    </VStack>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Status:</Text>
                      <Badge colorScheme={selectedQR.is_active ? 'green' : 'gray'}>
                        {selectedQR.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </VStack>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                leftIcon={<Icon as={FaDownload} />}
                onClick={() => selectedQR && downloadQRCode(selectedQR)}
              >
                Download
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
